/**
 * Rotas do Prêmio ELBB — copiadas de origin/main-elbb:src/routes/index.tsx.
 * O diff desta tabela contra a referência deve mostrar apenas diferenças de imports.
 * Telas compartilhadas usam a versão desta branch (out-of-scope per spec HOME-01).
 */
import { useEffect, useRef } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';

import { useAuth } from 'context/AuthContext';

import Spinner from 'components/ui/Spinner';
import { ActivePhases, useActivePhases } from 'hooks/useControlDate';
import { getProduto } from 'config/produto-env';
import { LoginBanca } from 'pages/Auth/LoginBanca';
import { LoginElbb } from 'pages/Auth/LoginElbb';
import { LoginPublic } from 'pages/Auth/LoginPublic';
import { ClassificadasEtapa1Elbb } from 'pages/ClassificadasEtapa1Elbb';
import { ConfirmacaoElbb } from 'pages/ConfirmacaoElbb';
import { ConfirmacaoVotoEtapa1 } from 'pages/ConfirmacaoVotoEtapa1';
import { ConfirmacaoVotoEtapa2 } from 'pages/ConfirmacaoVotoEtapa2';
import { ConfirmacaoVotoEtapa3 } from 'pages/ConfirmacaoVotoEtapa3';
import { HomeElbb } from 'pages/HomeElbb';
import { HomePublic } from 'pages/HomePublic';
import { IndicadosElbb } from 'pages/IndicadosElbb';
import { Iniciativas } from 'pages/Iniciativas';
import { IniciativasElbb } from 'pages/IniciativasElbb';
import { InscricaoIniciativaElbb } from 'pages/InscricaoIniciativaElbb';
import { Listagem } from 'pages/Listagem';
import { ListagemInscricaoElbb } from 'pages/ListagemInscricaoElbb';
import { MeusVotos } from 'pages/MeusVotos';
import { MeusVotosEtapa2 } from 'pages/MeusVotosEtapa2';
import { MeusVotosEtapa3 } from 'pages/MeusVotosEtapa3';
import { MinhaJornada } from 'pages/MinhaJornada';
import { MinhaJornadaElbb } from 'pages/MinhaJornadaElbb';
import { MinhasIniciativasElbb } from 'pages/MinhasIniciativasElbb';
import { NotFoundElbb } from 'pages/NotFoundElbb';
import { RegulamentoElbb } from 'pages/RegulamentoElbb';
import { ValidacaoIniciativas } from 'pages/ValidacaoIniciativas';
import { VotacaoEtapa1 } from 'pages/VotacaoEtapa1';
import { VotacaoEtapa2 } from 'pages/VotacaoEtapa2';
import { VotacaoEtapa3 } from 'pages/VotacaoEtapa3';
import CustomNavigationClient from 'sso/azure/CustomNavigationClient';
import { resolverDestinoInicial } from 'utils/destino-inicial';
import { MinhasIniciativasElbbEligibleRoute } from './MinhasIniciativasElbbEligibleRoute';
import { PerfilIndisponivel } from 'pages/PerfilIndisponivel';
import { PreInscricaoFaseRoute } from './PreInscricaoFaseRoute';
import { PreInscricaoRoute } from './PreInscricaoRoute';
import { RedirecionarPreInscricao } from './RedirecionarPreInscricao';
import { PreInscricao } from 'pages/PreInscricao';
import { PrivateRoute } from './PrivateRoute';
import { PublicRoute } from './PublicRoute';

export const RotasElbb = ({ msalInstance }: any) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const navigationClient = new CustomNavigationClient(navigate);
  msalInstance.setNavigationClient(navigationClient);

  const InitialRedirect = () => {
    const phases = useActivePhases();
    const hasRedirectedRef = useRef(false);

    useEffect(() => {
      if (!user || hasRedirectedRef.current) return;
      hasRedirectedRef.current = true;

      const destino = resolverDestinoInicial({
        perfil: user.perfil,
        pessoa: user.pessoa,
        fases: phases,
        produto: getProduto(),
      });
      console.log(`[InitialRedirect] Redirecionando -> ${destino}`);
      navigate(destino);
    }, [phases]);

    return (
      <div className="flex items-start justify-center w-full h-screen">
        <div className="flex flex-col">
          <Spinner />
          <span className="text-center text-blue-bb">Carregando...</span>
        </div>
      </div>
    );
  };

  return (
    <Routes>
      <Route
        path="/"
        element={
          <PrivateRoute redirectTo="/login">
            {!user ? (
              <div className="flex items-start justify-center w-full h-screen">
                <div className="flex flex-col">
                  <Spinner />
                  <span className="text-center text-blue-bb">
                    Carregando...
                  </span>
                </div>
              </div>
            ) : (
              <InitialRedirect />
            )}
          </PrivateRoute>
        }
      />

      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginElbb />
          </PublicRoute>
        }
      />

      <Route
        path="/login-public"
        element={
          <PublicRoute>
            <LoginPublic />
          </PublicRoute>
        }
      />

      <Route
        path="/login-elbb"
        element={
          <PublicRoute>
            <LoginElbb />
          </PublicRoute>
        }
      />

      <Route
        path="/login-banca"
        element={
          <PublicRoute>
            <LoginBanca />
          </PublicRoute>
        }
      />

      <Route
        path="/home-public"
        element={
          <PrivateRoute redirectTo="/login">
            <HomePublic />
          </PrivateRoute>
        }
      />

      <Route
        path="/home-elbb"
        element={
          <PrivateRoute redirectTo="/login-elbb">
            <HomeElbb />
          </PrivateRoute>
        }
      />

      {/* Regulamento ELBB: rota pública, sem guard */}
      <Route path="/regulamento-elbb" element={<RegulamentoElbb />} />

      <Route
        path="/iniciativas"
        element={
          <PrivateRoute redirectTo="/login">
            <Iniciativas />
          </PrivateRoute>
        }
      />

      <Route
        path="/iniciativas-elbb"
        element={
          <PrivateRoute redirectTo="/login-elbb">
            <IniciativasElbb />
          </PrivateRoute>
        }
      />

      <Route
        path="/indicados-elbb"
        element={
          <PrivateRoute redirectTo="/login-elbb">
            <IndicadosElbb />
          </PrivateRoute>
        }
      />

      <Route
        path="/classificadas-etapa-1-elbb"
        element={
          <PrivateRoute redirectTo="/login-elbb">
            <ClassificadasEtapa1Elbb />
          </PrivateRoute>
        }
      />

      <Route
        path="/minha-jornada"
        element={
          <PrivateRoute redirectTo="/login">
            <MinhaJornada />
          </PrivateRoute>
        }
      />

      <Route
        path="/minha-jornada-elbb"
        element={
          <PrivateRoute redirectTo="/login-elbb">
            <MinhaJornadaElbb />
          </PrivateRoute>
        }
      />

      <Route
        path="/minhas-iniciativas-elbb"
        element={
          <PrivateRoute redirectTo="/login-elbb">
            <MinhasIniciativasElbbEligibleRoute>
              <MinhasIniciativasElbb />
            </MinhasIniciativasElbbEligibleRoute>
          </PrivateRoute>
        }
      />

      <Route
        path="/pre-inscricao"
        element={
          <PrivateRoute redirectTo="/login">
            <PreInscricaoFaseRoute>
              <PreInscricaoRoute>
                <PreInscricao />
              </PreInscricaoRoute>
            </PreInscricaoFaseRoute>
          </PrivateRoute>
        }
      />

      {/* Rotas da pré-inscrição de 2026: levam ao formulário único, mantendo o modo edição. */}
      <Route path="/cadastroELBB" element={<RedirecionarPreInscricao />} />
      <Route path="/cadastroConexao" element={<RedirecionarPreInscricao />} />

      <Route
        path="/listagem"
        element={
          <PrivateRoute
            redirectTo="/login"
            requireNonManager={true}
          >
            <PreInscricaoFaseRoute listagem>
              <Listagem />
            </PreInscricaoFaseRoute>
          </PrivateRoute>
        }
      />

      <Route
        path="/validacaoIniciativas"
        element={
          <PrivateRoute
            redirectTo="/login"
            requireManager={true}
            requiredPhase={(p: ActivePhases) => p.elbbConfirmation}
          >
            <ValidacaoIniciativas />
          </PrivateRoute>
        }
      />

      <Route
        path="/inscricao-iniciativa-elbb/:id"
        element={
          <PrivateRoute redirectTo="/login-elbb">
            <InscricaoIniciativaElbb />
          </PrivateRoute>
        }
      />

      <Route
        path="/listagem-inscricao-elbb"
        element={
          <PrivateRoute redirectTo="/login-elbb">
            <ListagemInscricaoElbb />
          </PrivateRoute>
        }
      />

      <Route
        path="/confirmacao-elbb"
        element={
          <PrivateRoute redirectTo="/login-elbb">
            <ConfirmacaoElbb />
          </PrivateRoute>
        }
      />

      <Route
        path="/votacao-etapa-1"
        element={
          <PrivateRoute redirectTo="/login-elbb">
            <VotacaoEtapa1 />
          </PrivateRoute>
        }
      />

      <Route
        path="/meus-votos"
        element={
          <PrivateRoute redirectTo="/login-elbb">
            <MeusVotos />
          </PrivateRoute>
        }
      />

      <Route
        path="/confirmacao-voto-etapa1"
        element={
          <PrivateRoute redirectTo="/login-elbb">
            <ConfirmacaoVotoEtapa1 />
          </PrivateRoute>
        }
      />

      {/* No main-elbb esta rota era declarada duas vezes e valia a primeira, sem guard de fase */}
      <Route
        path="/votacao-etapa-2"
        element={
          <PrivateRoute redirectTo="/login-elbb">
            <VotacaoEtapa2 />
          </PrivateRoute>
        }
      />

      <Route
        path="/meus-votos-etapa-2"
        element={
          <PrivateRoute redirectTo="/login">
            <MeusVotosEtapa2 />
          </PrivateRoute>
        }
      />

      <Route
        path="/confirmacao-voto-etapa-2"
        element={
          <PrivateRoute
            redirectTo="/login"
            requiredPhase={(p: ActivePhases) => p.elbbVotingStage2}
          >
            <ConfirmacaoVotoEtapa2 />
          </PrivateRoute>
        }
      />

      <Route
        path="/votacao-etapa-3"
        element={
          <PrivateRoute
            redirectTo="/login-elbb"
            requiredPhase={(p: ActivePhases) => p.etapa3premio}
          >
            <VotacaoEtapa3 />
          </PrivateRoute>
        }
      />

      <Route
        path="/confirmacao-voto-etapa-3"
        element={
          <PrivateRoute
            redirectTo="/login-elbb"
            requiredPhase={(p: ActivePhases) => p.etapa3premio}
          >
            <ConfirmacaoVotoEtapa3 />
          </PrivateRoute>
        }
      />

      <Route
        path="/meus-votos-etapa-3"
        element={
          <PrivateRoute redirectTo="/login-elbb">
            <MeusVotosEtapa3 />
          </PrivateRoute>
        }
      />

      <Route
        path="/perfil-indisponivel"
        element={
          <PrivateRoute redirectTo="/login">
            <PerfilIndisponivel />
          </PrivateRoute>
        }
      />

      <Route path="*" element={<NotFoundElbb />} />
    </Routes>
  );
};
